package com.islamicmedia.shared.presentation

import com.islamicmedia.shared.domain.model.DisambiguationItem

/**
 * Local, allocation-light typeahead matcher over the cached titles index. Pure and synchronous —
 * called on every keystroke, so it must stay fast (the index is ~couple thousand entries).
 *
 * Ranking: a title-prefix beats a word-start beats a substring; ties break by the item's position
 * in [items], which is the server's popularity order. No fuzzy/typo tolerance yet (handled by the
 * "search anyway" fallback that hits the real disambiguation pipeline).
 */
object TitleAutocomplete {

    private const val MIN_QUERY_LEN = 2
    private const val MAX_RESULTS = 7

    fun rank(query: String, items: List<DisambiguationItem>, limit: Int = MAX_RESULTS): List<DisambiguationItem> {
        val q = normalize(query)
        if (q.length < MIN_QUERY_LEN || items.isEmpty()) return emptyList()

        // (score, originalIndex, item) — sort by score asc, then index asc (popularity tiebreak).
        val scored = ArrayList<Triple<Int, Int, DisambiguationItem>>()
        for ((index, item) in items.withIndex()) {
            val score = matchScore(q, normalize(item.title))
            if (score >= 0) scored.add(Triple(score, index, item))
        }
        scored.sortWith(compareBy({ it.first }, { it.second }))
        return scored.asSequence().take(limit).map { it.third }.toList()
    }

    /** Lower is better; -1 = no match. Inputs are already normalized. */
    private fun matchScore(q: String, title: String): Int = when {
        title.startsWith(q) -> 0          // "fro" → "frozen"
        wordStartsWith(title, q) -> 1     // "panda" → "kung fu panda"
        title.contains(q) -> 2            // substring anywhere
        else -> -1
    }

    /** True if [q] begins any word in [title] (a match at index 0 or just after a space). */
    private fun wordStartsWith(title: String, q: String): Boolean {
        var i = title.indexOf(q)
        while (i >= 0) {
            if (i == 0 || title[i - 1] == ' ') return true
            i = title.indexOf(q, i + 1)
        }
        return false
    }

    /**
     * lowercase → fold common Latin diacritics (Pokémon→pokemon) → "&"→"and" → drop punctuation
     * to spaces → collapse runs of spaces → strip a leading "the ".
     */
    private fun normalize(s: String): String {
        val sb = StringBuilder(s.length)
        for (raw in s) {
            val c = fold(raw.lowercaseChar())
            when {
                c == '&' -> sb.append(" and ")
                c.isLetterOrDigit() -> sb.append(c)
                else -> sb.append(' ')
            }
        }
        var r = sb.toString().split(' ').filter { it.isNotEmpty() }.joinToString(" ")
        if (r.startsWith("the ")) r = r.substring(4)
        return r
    }

    private fun fold(c: Char): Char = when (c) {
        'á', 'à', 'â', 'ä', 'ã', 'å' -> 'a'
        'é', 'è', 'ê', 'ë' -> 'e'
        'í', 'ì', 'î', 'ï' -> 'i'
        'ó', 'ò', 'ô', 'ö', 'õ' -> 'o'
        'ú', 'ù', 'û', 'ü' -> 'u'
        'ñ' -> 'n'
        'ç' -> 'c'
        'ý', 'ÿ' -> 'y'
        else -> c
    }
}
